import React from 'react';
import {fromJS} from 'immutable';
import type {List, Map} from 'immutable';
import {renderMoment} from 'lib/timeUtils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import appStore from 'client/AppStore';
import {useFluxStore} from 'client/framework';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import Modal, {ModalContent} from '../Modal';
import {hideModal} from '../ModalActions';
import Tabs from 'sg/Tabs/Tabs.react';
import {MandarkTable, Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import Button from 'client/styleguide/Button/Button.react';
import {Icon} from '@albert-io/atomic';
import questionTrackerModalStore from './QuestionTrackerModal.store';
import './question-tracker.scss';

interface TrackedClassroom {
  getId: () => string;
}

interface TrackedAssignment {
  getId: () => string;
  getName: () => string;
  getClassroomNames: () => string;
  getDueDateFromSettings: () => string;
  getClassrooms: () => List<TrackedClassroom>;
}

interface TrackedTemplate {
  getId: () => string;
  getName: () => string;
}

// `fromJS` is typed as returning `unknown`; the shape is a literal right here, so
// assert it rather than rebuilding the nested Maps by hand.
const ASSIGNMENTS_TAB = fromJS([
  {
    label: 'Assignments',
    query: {
      questionTrackerType: null
    }
  }
]) as List<Map<string, unknown>>;

const TEMPLATES_TAB = fromJS({
  label: 'Folders',
  query: {
    questionTrackerType: 'templates'
  }
}) as Map<string, unknown>;

function closeModal(): void {
  hideModal('questionTracker');
}

function isTemplateTab(): boolean {
  const {query} = appStore.routerProps.location as {query: {questionTrackerType?: string}};
  return query.questionTrackerType === 'templates';
}

function getAssignmentDetailsContent(item: TrackedAssignment) {
  const classroomNames = item.getClassroomNames();
  const classroomNamesContent =
    classroomNames !== '' ? (
      <span>
        {classroomNames}
        <span className='question-tracker-modal__names-content-divider'>&#9679;</span>
      </span>
    ) : null;
  return (
    <div>
      <div className='question-association-details__name'>{item.getName()}</div>
      <div className='question-association-details__classroom-content'>
        {classroomNamesContent}
        {renderMoment(item.getDueDateFromSettings(), 'dateTime')}
      </div>
    </div>
  );
}

function getTemplateDetailsContent(item: TrackedTemplate) {
  return <div className='question-association-details__name'>{item.getName()}</div>;
}

function getAssignmentViewButtonContent(item: TrackedAssignment) {
  const classrooms = item.getClassrooms();
  if (classrooms.isEmpty()) {
    return null;
  }
  // Non-empty per the guard above.
  const classroom = classrooms.first()!.getId();
  const itemId = item.getId();
  return (
    <Button
      className='question-tracker-modal__view-button'
      color='white'
      onClick={closeModal}
      small
      to={`/classes/${classroom}/assignments/${itemId}/student-detail`}
    >
      View
    </Button>
  );
}

function getTemplateViewButtonContent(item: TrackedTemplate) {
  return (
    <Button
      className='question-tracker-modal__view-button'
      color='white'
      onClick={closeModal}
      small
      to={`/assignment/${item.getId()}`}
    >
      View
    </Button>
  );
}

export default function QuestionTrackerModal() {
  const teacherId = useFluxStore(masqueradeStore, (store) => store.getUserIdByMasqueradeState());
  const questionSetId = useFluxStore(questionTrackerModalStore, (store) =>
    store.getQuestionSetId()
  );

  const onTemplateTab = isTemplateTab();

  const query = onTemplateTab
    ? resource('template_v1')
        .mandarkEndpoint(['templates_v1'])
        .include('question_sets_v1')
        .filter({
          teacher_v1: teacherId,
          question_sets_v1: questionSetId
        })
    : resource('assignment_v3')
        .mandarkEndpoint(['assignments_v3'])
        .include('classrooms_v1')
        .include('subject_v2')
        .filter({
          classrooms_v1: {
            status: 'active'
          },
          teacher_v1: teacherId,
          question_sets_v1: questionSetId
        });

  return (
    <Modal hideHeader modalKey='questionTracker' modalClass='question-tracker-modal'>
      <ModalContent>
        <div
          className='question-tracker-modal__top-close-button simple-modal__close-btn'
          onClick={closeModal}
          role='button'
          aria-label='Close'
          tabIndex={0}
        >
          <Icon icon='xmark' />
        </div>
        <div className='question-tracker-modal__header'>
          <div className='question-tracker-modal__header-name'>Question usage</div>
          <div className='question-tracker-modal__header-description'>
            This question is used in the following places. Note that the assignment list reflects
            assignments for active classrooms only.
          </div>
        </div>
        <Tabs content={ASSIGNMENTS_TAB.push(TEMPLATES_TAB)}>
          <MandarkTable mobileBreakpoint={600} query={query}>
            <Column content={onTemplateTab ? getTemplateDetailsContent : getAssignmentDetailsContent} />
            <Column
              content={onTemplateTab ? getTemplateViewButtonContent : getAssignmentViewButtonContent}
            />
          </MandarkTable>
        </Tabs>
        <div className='question-tracker-modal__bottom-close-button-wrapper'>
          <Button
            className='question-tracker-modal__bottom-close-button'
            color='green'
            onClick={closeModal}
          >
            Done
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
